import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  DollarSign,
  Users,
  Target,
  TrendingUp,
  MousePointer,
  Eye,
  Globe,
  MessageCircle,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { getCacheWarmStatus } from '../api/settings';
import { getJobPromotionsSummary } from '../api/insights';
import { formatCurrency, formatNumber, formatPercent, formatRatio } from '../lib/utils';
import { useAccounts } from '../hooks/useAccounts';
import {
  useAggregatedSummary,
  useWebsiteStats,
  useDmStats,
} from '../hooks/useAggregatedInsights';
import KPICard from '../components/dashboard/KPICard';
import DateRangeSelector from '../components/dashboard/DateRangeSelector';
import AccountSelector from '../components/dashboard/AccountSelector';
import TopBottomPerformers from '../components/dashboard/TopBottomPerformers';
import { useDateRangeStore } from '../store/dateRangeStore';
import { useAccountStore } from '../store/accountStore';

export default function Dashboard() {
  const { t } = useTranslation();
  const { dateRange, setDateRange } = useDateRangeStore();
  const { selectedAccountId, setSelectedAccountId } = useAccountStore();

  const { data: accounts } = useAccounts();

  const {
    data: summary,
    isLoading: summaryLoading,
  } = useAggregatedSummary({
    selectedAccountId,
    dateRange: 'custom',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const { data: websiteStats, isLoading: websiteLoading } = useWebsiteStats({
    dateRange: 'custom',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const { data: dmStats, isLoading: dmLoading } = useDmStats({
    dateRange: 'custom',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Empty selectedAccountId means "All Accounts" — only there do we blend the
  // Zoho-direct signals (Website + manual DM) into the headline KPIs and show
  // the dedicated sections. A specific account view stays at ad-account scope.
  const isAllAccounts = !selectedAccountId;

  // Job-promotion spend lives OUTSIDE the account summaries (T.B's account
  // view is filtered to השמה ads; the job campaigns are its complement), so
  // the "all spend" tile adds it explicitly.
  const { data: jobPromoSummary } = useQuery({
    queryKey: ['job-promotions-summary', dateRange.startDate, dateRange.endDate],
    queryFn: () => getJobPromotionsSummary(dateRange.startDate, dateRange.endDate),
    staleTime: 30 * 60 * 1000,
    enabled: isAllAccounts,
  });
  // Website is deliberately NOT blended into the headline KPIs — those leads
  // and deals belong to the Website section alone, so the top row stays
  // "what the ads (+ DM) produced".
  const blendLeads = isAllAccounts ? (dmStats?.totalLeads ?? 0) : 0;
  const blendDeals = isAllAccounts ? (dmStats?.closedDeals ?? 0) : 0;
  const blendRevenue = isAllAccounts ? (dmStats?.totalRevenue ?? 0) : 0;
  const blendedLeads = (summary?.totalLeads ?? 0) + blendLeads;
  const blendedDeals = (summary?.totalDeals ?? 0) + blendDeals;
  const blendedRevenue = (summary?.totalRevenue ?? 0) + blendRevenue;
  const blendedRoas =
    summary && summary.totalSpend > 0 ? blendedRevenue / summary.totalSpend : null;
  const blendedCpl =
    summary && summary.totalSpend > 0 && blendedLeads > 0
      ? summary.totalSpend / blendedLeads
      : null;
  const blendedCostPerDeal =
    summary && summary.totalSpend > 0 && blendedDeals > 0
      ? summary.totalSpend / blendedDeals
      : null;

  // Freshness pill uses the SERVER's warm-cache timestamp (when the nightly
  // job or the manual "Refresh data" button last populated the cache) —
  // that's the true age of the numbers, regardless of when the browser
  // last hit the API. The earlier `lastRefreshed` state tracked browser
  // fetch time, which lied whenever TanStack served a cached response.
  const { data: cacheWarmStatus } = useQuery({
    queryKey: ['cache-warm-status'],
    queryFn: getCacheWarmStatus,
    refetchInterval: 60_000,
    staleTime: 60_000,
  });
  const lastRefreshed = cacheWarmStatus?.lastWarmedAt
    ? new Date(cacheWarmStatus.lastWarmedAt)
    : null;
  const [, forceRelativeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceRelativeTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Show placeholder if no accounts exist at all
  if (!accounts?.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('accounts.addAccount')}
          </h2>
          <p className="mt-2 text-gray-500">
            Add an ad account to see your insights
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>

        <div className="flex items-center gap-4">
          <AccountSelector
            value={selectedAccountId}
            onChange={setSelectedAccountId}
          />
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
          <div className="flex items-center gap-2">
            {lastRefreshed && (
              <span
                className="text-xs text-gray-500"
                title={format(lastRefreshed, 'PPpp')}
              >
                {t('dashboard.updatedRelative', {
                  relative: formatDistanceToNow(lastRefreshed, { addSuffix: true }),
                })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content with Performers Sidebar */}
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* KPI Grid - Row 1 */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              title={t('kpis.spend')}
              value={summary ? formatCurrency(summary.totalSpend) : '-'}
              icon={<DollarSign className="h-5 w-5" />}
              loading={summaryLoading}
            />
            {isAllAccounts && (
              <KPICard
                title={t('kpis.allSpend')}
                value={
                  summary
                    ? formatCurrency(
                        summary.totalSpend + (jobPromoSummary?.totalSpend ?? 0)
                      )
                    : '-'
                }
                icon={<Wallet className="h-5 w-5" />}
                loading={summaryLoading}
                subtitle={t('kpis.allSpendSub')}
              />
            )}
            <KPICard
              title={t('kpis.leads')}
              value={
                summary || (isAllAccounts && dmStats)
                  ? formatNumber(blendedLeads)
                  : '-'
              }
              icon={<Users className="h-5 w-5" />}
              loading={summaryLoading || (isAllAccounts && dmLoading)}
              subtitle={isAllAccounts ? 'Ads + DM' : undefined}
            />
            <KPICard
              title={t('kpis.deals')}
              value={
                summary || (isAllAccounts && dmStats)
                  ? formatNumber(blendedDeals)
                  : '-'
              }
              icon={<Target className="h-5 w-5" />}
              loading={summaryLoading || (isAllAccounts && dmLoading)}
              subtitle={isAllAccounts ? 'Ads + DM' : undefined}
            />
            <KPICard
              title={t('kpis.revenue')}
              value={
                summary || (isAllAccounts && dmStats)
                  ? formatCurrency(blendedRevenue)
                  : '-'
              }
              icon={<TrendingUp className="h-5 w-5" />}
              loading={summaryLoading || (isAllAccounts && dmLoading)}
              subtitle={isAllAccounts ? 'Ads + DM' : undefined}
            />
          </div>

          {/* KPI Grid - Row 2 */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KPICard
              title={t('kpis.cpl')}
              value={blendedCpl != null ? formatCurrency(blendedCpl) : '-'}
              icon={<DollarSign className="h-5 w-5" />}
              loading={summaryLoading || (isAllAccounts && dmLoading)}
              subtitle={isAllAccounts ? 'Ad spend / (Ads + DM leads)' : 'Cost per Lead'}
            />
            <KPICard
              title={t('kpis.costPerDeal')}
              value={blendedCostPerDeal != null ? formatCurrency(blendedCostPerDeal) : '-'}
              icon={<Target className="h-5 w-5" />}
              loading={summaryLoading || (isAllAccounts && dmLoading)}
              subtitle={isAllAccounts ? 'Ad spend / (Ads + DM deals)' : 'Cost per Deal Won'}
            />
            <KPICard
              title={t('kpis.roas')}
              value={blendedRoas != null ? formatRatio(blendedRoas) : '-'}
              icon={<TrendingUp className="h-5 w-5" />}
              loading={summaryLoading || (isAllAccounts && dmLoading)}
              subtitle={
                isAllAccounts
                  ? '(Ads + DM revenue) / Ad spend'
                  : 'Return on Ad Spend'
              }
            />
          </div>

          {/* KPI Grid - Row 3 (Engagement) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KPICard
              title={t('kpis.impressions')}
              value={summary ? formatNumber(summary.totalImpressions) : '-'}
              icon={<Eye className="h-5 w-5" />}
              loading={summaryLoading}
            />
            <KPICard
              title={t('kpis.clicks')}
              value={summary ? formatNumber(summary.totalClicks) : '-'}
              icon={<MousePointer className="h-5 w-5" />}
              loading={summaryLoading}
            />
            <KPICard
              title={t('kpis.ctr')}
              value={summary ? formatPercent(summary.avgCtr) : '-'}
              icon={<MousePointer className="h-5 w-5" />}
              loading={summaryLoading}
              subtitle="Click-through Rate"
            />
          </div>

          {/* Website / אתר section — only shown on the "All Accounts" view, since
              the Website signal isn't attributable to a specific ad account. */}
          {isAllAccounts && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-medium text-gray-900">
                  {t('dashboard.websiteSectionTitle')}
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <KPICard
                  title={t('kpis.websiteLeads')}
                  value={websiteStats ? formatNumber(websiteStats.totalLeads) : '-'}
                  icon={<Users className="h-5 w-5" />}
                  loading={websiteLoading}
                />
                <KPICard
                  title={t('kpis.websiteDealsWon')}
                  value={websiteStats ? formatNumber(websiteStats.closedDeals) : '-'}
                  icon={<Target className="h-5 w-5" />}
                  loading={websiteLoading}
                />
                <KPICard
                  title={t('kpis.websiteRevenue')}
                  value={websiteStats ? formatCurrency(websiteStats.totalRevenue) : '-'}
                  icon={<DollarSign className="h-5 w-5" />}
                  loading={websiteLoading}
                />
              </div>
            </div>
          )}

          {/* DM / manual leads section — All Accounts only, Lead_Source="dm" in Zoho. */}
          {isAllAccounts && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-medium text-gray-900">
                  {t('dashboard.dmSectionTitle')}
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <KPICard
                  title={t('kpis.dmLeads')}
                  value={dmStats ? formatNumber(dmStats.totalLeads) : '-'}
                  icon={<Users className="h-5 w-5" />}
                  loading={dmLoading}
                />
                <KPICard
                  title={t('kpis.dmDealsWon')}
                  value={dmStats ? formatNumber(dmStats.closedDeals) : '-'}
                  icon={<Target className="h-5 w-5" />}
                  loading={dmLoading}
                />
                <KPICard
                  title={t('kpis.dmRevenue')}
                  value={dmStats ? formatCurrency(dmStats.totalRevenue) : '-'}
                  icon={<DollarSign className="h-5 w-5" />}
                  loading={dmLoading}
                />
              </div>
            </div>
          )}

        </div>

        {/* Top/Bottom Performers Sidebar */}
        <div className="hidden w-72 flex-shrink-0 xl:block">
          <TopBottomPerformers />
        </div>
      </div>
    </div>
  );
}
